import { BaseService } from './BaseService';
import { Connector, ConnectorListResponse, UserConnector, UserConnectorListResponse } from '../models/app/mcp/Tool';

// 타입 re-export (기존 import 경로 호환용)
export type { Connector, ConnectorListResponse, UserConnector, UserConnectorListResponse };

export interface KmsSearchItem {
  id: string;
  title: string;
  url: string;
  type: 'space' | 'page';
}

export class ConnectorService extends BaseService {
  // 사용 가능한 커넥터 목록 조회 (도구 추가 화면용)
  public getAvailableConnectors = async (type?: string): Promise<Connector[]> => {

    const query = new URLSearchParams();
    if (type) {
      query.append('type', type);
    }

    const response = await this.getResponseAsync<Connector[] | ConnectorListResponse>({
      commandPath: 'api/connector/available',
      query: query,
      method: 'GET',
    });

    console.log('[API] getAvailableConnectors response:', response);

    // API 응답이 배열인 경우와 객체인 경우 모두 처리
    if (Array.isArray(response)) {
      return response;
    }

    // 기존 객체 형식 호환
    if ('connectors' in response && Array.isArray(response.connectors)) {
      return response.connectors;
    }

    return [];
  };

  public setAvailableConnectors = async (connectors: Connector[]): Promise<void> => {
    await this.getResponseAsync<unknown>({
      commandPath: 'api/connector/available',
      method: 'POST',
      body: connectors,
    });
  };

  // 새 커넥터 생성 (도구 추가 화면에서 사용)
  public createConnector = async (connector: Connector): Promise<UserConnector> =>
    this.getResponseAsync<UserConnector>({
      commandPath: 'api/connector',
      method: 'POST',
      body: connector,
    });

  // 커넥터 업데이트 (토글 스위치 등에서 사용)
  public updateConnector = async (userConnector: UserConnector): Promise<UserConnector> =>
    this.getResponseAsync<UserConnector>({
      commandPath: 'api/connector',
      method: 'PUT',
      body: userConnector,
    });

  public updateConnectorTool = async (connectorId: string, toolName: string, enabled: boolean): Promise<UserConnector> =>
    this.getResponseAsync<UserConnector>({
      commandPath: `api/connector/tool/${connectorId}`,
      method: 'PUT',
      body: {
        toolName: toolName,
        enabled: enabled
      }
    });


  // 커넥터 삭제 (연결 해제)
  public deleteConnector = async (id: string): Promise<void> => {
    await this.getResponseAsync<unknown>({
      commandPath: `api/connector?id=${encodeURIComponent(id)}`,
      method: 'DELETE',
    });
  };

  // 사용자 커넥터 목록 조회 (도구 화면용)
  public getUserConnectors = async (count?: number, isEnabled?: boolean, type?: string): Promise<UserConnectorListResponse> => {
    const commandPath = 'api/connector/list';

    const query = new URLSearchParams();

    if (count !== undefined) {
      query.append('count', String(count));
    }
    if (isEnabled !== undefined) {
      query.append('isEnabled', String(isEnabled));
    }

    if (type) {
      query.append('type', type);
    }

    const response = await this.getResponseAsync<UserConnectorListResponse>({
      commandPath,
      query: query,
      method: 'GET',
    });

    // console.log('[API] getUserConnectors response:', response);
    return response;
  };

  /**
   * 사용자 커넥터 목록 동기화 (도구 설정 화면 최초 로드 시 사용)
   * - available에 없는 유저 커넥터는 삭제
   * - available + userConnectors 둘 다 반환 (중복 API 호출 방지)
   */
  public syncUserConnectors = async (): Promise<{
    available: Connector[];
    userConnectors: UserConnectorListResponse;
  }> => {
    // 1. available 커넥터와 유저 커넥터 목록 동시 조회
    const [availableConnectors, userResponse] = await Promise.all([
      this.getAvailableConnectors('all'),
      this.getUserConnectors(undefined, undefined, 'all'),
    ]);

    const availableNames = new Set<string | null>(
      availableConnectors.map((c: Connector) => c.name)
    );

    const userConnectors = userResponse.data ?? [];

    // 2. available에 없는 유저 커넥터 찾기
    const orphanedConnectors = userConnectors.filter(
      (uc: UserConnector) => uc.connectorType && !availableNames.has(uc.connectorType)
    );

    // 3. 고아 커넥터 삭제
    if (orphanedConnectors.length > 0) {
      console.log(`[ConnectorService] Removing ${orphanedConnectors.length} orphaned connectors:`,
        orphanedConnectors.map((c: UserConnector) => c.connectorType));

      await Promise.all(
        orphanedConnectors.map((uc: UserConnector) => uc.id && this.deleteConnector(uc.id))
      );

      // 4. 삭제 후 목록 다시 조회
      return {
        available: availableConnectors,
        userConnectors: await this.getUserConnectors(),
      };
    }

    // 삭제할 것 없으면 기존 응답 반환
    return {
      available: availableConnectors,
      userConnectors: userResponse,
    };
  };

  // KMS(Confluence) 검색
  public searchKms = async (q: string): Promise<KmsSearchItem[]> => {
    const query = new URLSearchParams();
    query.append('q', q);
    const response = await this.getResponseAsync<KmsSearchItem[]>({
      commandPath: 'api/connector/kms/search',
      query,
      method: 'GET',
    });
    return Array.isArray(response) ? response : [];
  };
}

